import * as React from 'react';
import {
    assumeDisplayDate,
    formatUserTime,
    parseUserTime,
    getYear,
    getMonth,
    getDate,
    getHours,
    getMinutes,
    getSeconds,
    getMilliseconds,
    getTimestamp,
    OwaDate,
    owaDate,
    addDays,
    addMinutes,
    asDate,
    isAfter,
    startOfDay,
} from 'owa-datetime';
import { ComboBox, IComboBox, IComboBoxOption, IComboBoxStyles } from 'office-ui-fabric-react/lib';
import { getPalette } from 'owa-theme';
import { getUserConfiguration } from 'owa-session-store';
import { IButtonStyles } from 'office-ui-fabric-react/lib/Button';
import { IComboBoxOptionStyles } from 'office-ui-fabric-react/lib/ComboBox';
import { observer } from 'mobx-react';
import { TimePickerProps } from './TimePicker.Props';
import formatEndTime from '../utils/formatEndTime';

let classNames = require('classnames');
let styles = require('./TimePicker.scss');

interface TimePickerOption extends IComboBoxOption {
    date: OwaDate /* Date value associated with the option */;
}

const chevronStyles: Partial<IButtonStyles> = {
    root: styles.chevronRoot,
    icon: styles.chevronIcon,
};

const comboBoxStylesWithoutLowerContent: Partial<IComboBoxStyles> = {
    optionsContainerWrapper: styles.optionsContainerWrapperStyles,
};

const comboBoxStylesWithLowerContent: Partial<IComboBoxStyles> = {
    optionsContainerWrapper: styles.lowerContent,
};

/**
 * Component uses fabric ComboBox to select time.
 */
@observer
export default class TimePicker extends React.Component<TimePickerProps> {
    private comboBox = React.createRef<IComboBox>();

    public componentDidMount() {
        if (this.props.setFocusOnMount) {
            this.comboBox.current.focus();
        }
    }

    private comboBoxOptionStyle = (): Partial<IComboBoxOptionStyles> => {
        let themeSymbols = getPalette();
        const { timePickerColorScheme } = this.props;
        const rowHoverColor = timePickerColorScheme
            ? timePickerColorScheme.rowHoverColor
            : themeSymbols.neutralLighter;
        const selectedRowColor = timePickerColorScheme
            ? timePickerColorScheme.selectedRowColor
            : themeSymbols.themeLighter;

        return {
            root: {
                whiteSpace: 'nowrap',
                backgroundColor: themeSymbols.white,
            },
            rootHovered: {
                backgroundColor: rowHoverColor,
            },
            rootChecked: {
                backgroundColor: selectedRowColor,
            },
        };
    };

    render() {
        const { readOnly, className, ariaLabel, onBlur, selectedTime } = this.props;
        const options = this.createTimeOptions();

        const comboBoxStyles = this.props.onRenderLowerContent
            ? comboBoxStylesWithLowerContent
            : comboBoxStylesWithoutLowerContent;

        return (
            <div
                ref="root"
                className={classNames(
                    className,
                    {
                        [styles.grey]: readOnly,
                    },
                    styles.timePicker
                )}>
                <div ref="textFieldContainer" className={styles.timePickerWrapper}>
                    <ComboBox
                        id="TimePicker-ComboBox"
                        comboBoxOptionStyles={this.comboBoxOptionStyle()}
                        ariaLabel={ariaLabel}
                        componentRef={this.comboBox}
                        allowFreeform={true}
                        autoComplete="on"
                        value={formatUserTime(selectedTime)}
                        options={options}
                        onChanged={this.onChanged}
                        caretDownButtonStyles={chevronStyles}
                        scrollSelectedToTop={true}
                        selectedKey={getTimestamp(selectedTime)}
                        styles={comboBoxStyles}
                        onRenderLowerContent={this.props.onRenderLowerContent}
                        onRenderOption={this.onRenderOption}
                        onBlur={onBlur}
                        disabled={readOnly}
                        onMenuDismissed={this.props.onMenuDismissed}
                    />
                </div>
            </div>
        );
    }

    /**
     * onChanged: Callback issued when either:
     * 1) the selected option changes
     * 2) a manually edited value is submitted.
     *    In this case there may not be a matched option if allowFreeform is also true
     *   (and hence only value would be true, the other parameter would be null in this case)
     * @param {TimePickerOption} option option
     * @param {number} index option index
     * @param {string} value input value
     */
    private onChanged = (option: TimePickerOption, index: number, value: string) => {
        const newDate = option ? option.date : value ? this.getDateFromRawText(value) : null;
        if (newDate) {
            this.props.onChanged(newDate);
        }
    };

    private getDateFromRawText = (value: string): OwaDate | null => {
        const { selectedTime } = this.props;

        // TZ_BUG: once DisplayDate becomes OwaDate we can simplify this call/return.
        const newDate = parseUserTime(value, assumeDisplayDate(asDate(selectedTime)));
        return newDate
            ? owaDate(
                  selectedTime.tz,
                  getYear(newDate),
                  getMonth(newDate),
                  getDate(newDate),
                  getHours(newDate),
                  getMinutes(newDate),
                  getSeconds(newDate),
                  getMilliseconds(newDate)
              )
            : null;
    };

    /**
     * Return JSX element of each option in the callout list
     * @param {TimePickerOption} option option to be rendered in callout list
     */
    private onRenderOption = ({ date, text }: TimePickerOption): JSX.Element => {
        let { startTime, durationClassName, showDuration = true } = this.props;

        return (
            <div className={'ms-ComboBox-optionText'}>
                {text}
                {showDuration && startTime && (
                    <span className={durationClassName}>
                        {' ' + formatEndTime(date, startTime)}
                    </span>
                )}
            </div>
        );
    };

    /**
     * Create the TimeOption objects backing each context menu item in time picker
     */
    private createTimeOptions = (): TimePickerOption[] => {
        const { HourIncrement } = getUserConfiguration().UserOptions;
        const { minutesInterval, startTime, selectedTime } = this.props;
        const timeInterval = minutesInterval || HourIncrement;

        // When startTime is specified we must make sure we move it to the same time zone
        // as the selectedTime. Cases like the Calendar Event compose allow start and
        // end times to be in different time zones and we don't want to mix them.
        const startTimeInCorrectTz = startTime
            ? owaDate(selectedTime.tz, startTime)
            : startOfDay(selectedTime);

        const end = addDays(startOfDay(startTimeInCorrectTz), 1);

        let time = startTimeInCorrectTz;
        const dates: OwaDate[] = [];
        while (isAfter(end, time)) {
            dates.push(time);
            let newTime = addMinutes(time, timeInterval);

            // When DST ends the clock winds backwards and we end up with duplicated hours (ex, Nov 4 2018 1am PST).
            // Although this is a valid date, we don't want to show it, so we skip it.
            // TZ_BUG: 38589: fpintos: why would we not show the 2nd 1am? Without it, the user can't create
            // events with certain durations. For example, if I want a 2h event starting at 12a I can't do it.
            const offsetDiff = newTime.getTimezoneOffset() - time.getTimezoneOffset();
            if (offsetDiff > 0) {
                newTime = addMinutes(newTime, offsetDiff);
            }

            time = newTime;
        }

        return dates.map(date => ({
            date,
            text: formatUserTime(date),
            key: getTimestamp(date),
        }));
    };
}
